import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials, statsData } from '@/mocks/animeData';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9omtbaqs38sj6jc7bl0';

export default function AboutPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于51动漫 - 品牌介绍与联系方式',
    description: '51动漫品牌网站导航，提供好看的动漫大全、海量韩漫日漫国漫免费全集，支持追更下拉式阅读。了解51动漫的品牌故事与联系方式。',
    url: 'https://51anime.com/about',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51anime.com' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Page Header */}
        <section className="relative pt-24 pb-12 md:pt-28 md:pb-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]} />
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4">关于51动漫</h1>
            <p className="text-foreground-500 text-sm mt-2 max-w-2xl">品牌介绍、用户口碑与联系方式</p>
          </div>
        </section>

        <main>
          {/* Brand Introduction */}
          <section className="py-12 md:py-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-primary-500 rounded-xl">
                  <span className="text-background-50 font-bold text-xl">51</span>
                </div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">品牌介绍</h2>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-3xl mx-auto">
                  51动漫成立于2020年，是国内领先的动漫内容聚合与导航平台。我们致力于为广大动漫爱好者提供一站式动漫观看服务，汇聚海量<strong>韩漫</strong>、<strong>日漫</strong>和<strong>国漫</strong>优质资源。平台以用户阅读体验为核心，首创下拉式阅读模式，让追更加流畅自然。经过多年发展，51动漫网已累计服务超过50万日活用户，成为动漫爱好者心目中首选的免费动漫网站。
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
                {statsData.map((stat) => (
                  <div key={stat.label} className="text-center p-5 bg-background-50 rounded-lg border border-background-200/70">
                    <div className="text-2xl font-bold text-primary-600 font-heading">{stat.value}</div>
                    <div className="text-foreground-500 text-xs mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Features */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    icon: 'ri-stack-line',
                    title: '海量资源',
                    desc: '涵盖韩漫、日漫、国漫三大品类，热门新番、经典老番一应俱全，持续更新扩充中。',
                  },
                  {
                    icon: 'ri-file-list-2-line',
                    title: '下拉式阅读',
                    desc: '创新的下拉式阅读模式，告别传统翻页，上下滑动即可畅享流畅阅读体验。',
                  },
                  {
                    icon: 'ri-flashlight-line',
                    title: '极速更新',
                    desc: '与各大漫画平台同步更新，热门连载第一时间同步上线，追更党永不错过。',
                  },
                ].map((feature) => (
                  <div key={feature.title} className="text-center p-6">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                      <i className={`${feature.icon} text-accent-600 text-xl`} />
                    </div>
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2">{feature.title}</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-12 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">用户口碑</h2>
                <p className="text-foreground-500 text-sm">我们的用户怎么说</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                {testimonials.map((t, i) => (
                  <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-6">
                    <div className="flex items-center gap-1 mb-4">
                      {Array.from({ length: t.rating }).map((_, j) => (
                        <i key={j} className="ri-star-fill text-accent-500 text-sm" />
                      ))}
                    </div>
                    <p className="text-foreground-600 text-sm leading-relaxed mb-4">&ldquo;{t.comment}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                      <span className="font-medium text-foreground-900 text-sm">{t.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="py-12 md:py-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">联系我们</h2>
                <p className="text-foreground-500 text-sm">有任何问题或建议？欢迎联系我们</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Contact Info */}
                <div className="space-y-6">
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-mail-line text-primary-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-foreground-900 text-sm">电子邮箱</h5>
                      <p className="text-foreground-500 text-sm">contact@51anime.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-wechat-line text-accent-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-foreground-900 text-sm">微信公众号</h5>
                      <p className="text-foreground-500 text-sm">51动漫官方</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 flex-shrink-0">
                      <i className="ri-bilibili-line text-secondary-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-foreground-900 text-sm">B站官方号</h5>
                      <p className="text-foreground-500 text-sm">51动漫官方频道</p>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-6">
                  <h5 className="font-semibold text-foreground-900 text-sm mb-5">发送消息</h5>
                  <form
                    data-readdy-form=""
                    action={CONTACT_FORM_URL}
                    method="POST"
                    encType="application/x-www-form-urlencoded"
                    className="space-y-4"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value.trim();
                      if (honeypot) return;

                      const formData = new FormData(form);
                      try {
                        const res = await fetch(form.action, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                          body: new URLSearchParams(formData as any).toString(),
                        });
                        const text = await res.text();
                        let parsed;
                        try { parsed = JSON.parse(text); } catch { parsed = null; }
                        if (res.ok && parsed?.code === 'OK') {
                          const msgEl = form.querySelector('.form-status') as HTMLElement;
                          if (msgEl) { msgEl.textContent = '消息已发送，感谢您的反馈！'; msgEl.className = 'form-status text-xs text-green-600 mt-3'; }
                          form.reset();
                        } else {
                          const msgEl = form.querySelector('.form-status') as HTMLElement;
                          if (msgEl) { msgEl.textContent = parsed?.meta?.message || '发送失败，请稍后再试。'; msgEl.className = 'form-status text-xs text-red-500 mt-3'; }
                        }
                      } catch {
                        const msgEl = form.querySelector('.form-status') as HTMLElement;
                        if (msgEl) { msgEl.textContent = '网络错误，请稍后再试。'; msgEl.className = 'form-status text-xs text-red-500 mt-3'; }
                      }
                    }}
                  >
                    {/* Honeypot */}
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />

                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      />
                      <p className="text-foreground-400 text-[10px] mt-1">最多500个字符</p>
                    </div>
                    <button
                      type="submit"
                      className="w-full px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer"
                    >
                      发送消息
                    </button>
                    <p className="form-status text-xs mt-3" />
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}